import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { Select, IProps as SelectProps } from 'components/atoms/Select/Select.components';
import { useGetCountryPhoneCodesQuery } from 'services/general';

interface PhoneCodeSelectProps extends Omit<SelectProps, 'options' | 'handleChange'> {
  handleChange: (value?: string) => void;
  defaultValue?: string;
  name?: any;
}

export default function PhoneCodeSelect({
  name,
  defaultValue,
  value,
  ...rest
}: Readonly<PhoneCodeSelectProps>) {
  const { setValue, getValues } = useFormContext();
  const { data: countries = [], isLoading } = useGetCountryPhoneCodesQuery();
  
  const options = countries.map(country => ({
    value: `+${country.phonecode}`,
    label: `+${country.phonecode} (${country.sortname})`,
  }));

  const normalizePhoneCode = (code: string): string => {
    if (!code) return '';
    return code.startsWith('+') ? code : `+${code}`;
  };

  const findOptionByValue = (searchValue: string) => {
    const normalizedValue = normalizePhoneCode(searchValue);
    return options.find(option => option.value === normalizedValue);
  };

  useEffect(() => {
    if (isLoading || !countries.length) return;
    
    if (defaultValue && !getValues('phoneCode')) {
      const formattedValue = normalizePhoneCode(defaultValue);
      setValue('phoneCode', formattedValue, { shouldValidate: true });
    }
  }, [isLoading, countries.length, defaultValue, setValue, getValues]);

  useEffect(() => {
  const currentFormValue = getValues('phoneCode');
  if (currentFormValue && !isLoading && countries.length > 0) {
    const option = findOptionByValue(currentFormValue);
    if (option && (!value || !value.value)) {
      setValue('phoneCode', currentFormValue, { shouldValidate: false });
    }
  }
}, []);

  const getCurrentValue = () => {
    if (value && typeof value === 'object' && 'value' in value) {
      return value;
    }
    
    const currentPhoneCode = getValues('phoneCode') || '';
    if (currentPhoneCode) {
      const option = findOptionByValue(currentPhoneCode);
      if (option) {
        return {
          label: option.label,
          value: option.value
        };
      }
    }
    
    return {
      label: '',
      value: ''
    };
  };

  return (
    <Select
      options={options}
      value={getCurrentValue()}
      {...rest}
      disabled={rest.disabled || isLoading}
      handleChange={(selectedValue) => {
        const phoneCode = selectedValue?.toString() ?? '';

        rest.handleChange(phoneCode);

        setValue('phoneCode', phoneCode, { shouldValidate: true });
      }}
    />
  );
}